import { useDebounceFn } from "ahooks";
import { Input, Select, Space } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import patientService from "@/services/patient";
import useMasterDataState from "@/stores/MasterDataState";
import type { IPhonePickerComponentProps } from "./_models";

const { Option } = Select;

export const validateSingaporePhone = (countryCode: string, number: string): string | null => {
  if (countryCode === "SG" && number) {
    // Singapore phone numbers should be exactly 8 digits
    if (!/^\d{8}$/.test(number)) {
      return "Singapore phone numbers must be 8 digits";
    }

    // Singapore mobile numbers should start with 3, 6, 8 or 9
    if (!/^[3689]/.test(number)) {
      return "Singapore mobile numbers must start with 3, 6, 8 or 9";
    }

    // If starts with 9, second digit must be 0-8 (not 99)
    if (/^9/.test(number) && number.length >= 2) {
      if (!/^9[0-8]/.test(number)) {
        return "Numbers starting with 9 must have second digit between 0-8";
      }
    }
  }
  return null;
};

const PhonePickerComponent: React.FC<IPhonePickerComponentProps> = ({
  value,
  countryCodeValue = "SG",
  onChange,
  disabled,
  placeholder,
  error,
  isCheckDuplicate,
  containerStyle,
  initialValue,
  hideErrorMessage = false,
}) => {
  const [phoneNumber, setPhoneNumber] = useState<string>(initialValue?.phoneNumber || "");
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    initialValue?.countryCode || countryCodeValue,
  );
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneWarning, setPhoneWarning] = useState<string | null>(null);
  const masterDataState = useMasterDataState();
  const { id: patientId } = useParams();

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setPhoneNumber(value);
    }
    if (countryCodeValue) {
      setSelectedCountryCode(countryCodeValue);
    }
  }, [value, countryCodeValue]);

  const { run: validateDuplicatePhone } = useDebounceFn(
    async (countryCode: string, number: string) => {
      const dialCode = masterDataState.nationality.find(
        (country) => country.code === countryCode,
      )?.dialCode;
      if (isCheckDuplicate && number && number.length === 8) {
        try {
          const response = await patientService.softValidateIdAndMobileNumber({
            mobileNumber: number,
            mobileNumberRegionCode: dialCode,
            excludePatientId: patientId,
          });

          if (response.data?.mobileNumber?.exists) {
            setPhoneWarning("Already exists");
          } else {
            setPhoneWarning(null);
          }
        } catch (err) {
          console.error("Error validating phone:", err);
          setPhoneWarning(null);
        }
      } else {
        setPhoneWarning(null);
      }
    },
    { wait: 500 },
  );

  const handleCountryCodeChange = (value: string) => {
    setSelectedCountryCode(value);
    const validationError = validateSingaporePhone(value, phoneNumber);
    setPhoneError(validationError);

    validateDuplicatePhone(value, phoneNumber);
    const dialCode = masterDataState.nationality.find(
      (country) => country.code === value,
    )?.dialCode;
    if (onChange) {
      onChange({
        formattedNumber: `${dialCode} ${phoneNumber}`,
        countryCode: value,
        phoneNumber: phoneNumber,
        error: validationError,
      });
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (/^\d*$/.test(value) || value === "") {
      setPhoneNumber(value);
      const validationError = validateSingaporePhone(selectedCountryCode, value);
      setPhoneError(validationError);

      validateDuplicatePhone(selectedCountryCode, value);
      const dialCode = masterDataState.nationality.find(
        (country) => country.code === selectedCountryCode,
      )?.dialCode;
      if (onChange) {
        onChange({
          formattedNumber: `${dialCode} ${value}`,
          countryCode: selectedCountryCode,
          phoneNumber: value,
          error: validationError,
        });
      }
    }
  };

  return (
    <div className={containerStyle}>
      <Space.Compact style={{ width: "100%" }}>
        <Select
          disabled={disabled}
          size="large"
          value={selectedCountryCode}
          onChange={handleCountryCodeChange}
          style={{ width: "80px" }}
          showSearch
          optionFilterProp="children"
          popupMatchSelectWidth={false}
          styles={{
            popup: {
              root: {
                width: "300px",
              },
            },
          }}
          status={phoneWarning || phoneError ? "error" : undefined}
          filterOption={(input, option) => {
            if (!option?.label) return false;
            return (
              option.label.toString().toLowerCase().includes(input.toLowerCase()) ||
              (option.value as string).toLowerCase().includes(input.toLowerCase())
            );
          }}
          labelRender={(props) => {
            const country = masterDataState.nationality.find(
              (country) => country.code === props.value,
            );
            return (
              <div className="flex items-center gap-2">
                {country?.flagUrl && (
                  <img src={country.flagUrl} width={"20px"} height={"20px"} alt={country.country} />
                )}
              </div>
            );
          }}
        >
          {masterDataState.nationality.map((country) => (
            <Option
              key={country.code}
              value={country.code}
              label={`${country.country} ${country.dialCode}`}
            >
              <div className="flex items-center gap-2">
                {country.flagUrl && (
                  <img src={country.flagUrl} width={"20px"} height={"20px"} alt={country.country} />
                )}
                <span>{country.dialCode}</span>
                <span>{country.country}</span>
              </div>
            </Option>
          ))}
        </Select>

        <Input
          size="large"
          disabled={disabled}
          prefix={
            <span className="text-black">
              {
                masterDataState.nationality.find((country) => country.code === selectedCountryCode)
                  ?.dialCode
              }
            </span>
          }
          value={phoneNumber}
          allowClear
          onChange={handlePhoneNumberChange}
          placeholder={placeholder || "12345678"}
          maxLength={12}
          style={{ width: "100%" }}
          status={phoneError || error ? "error" : phoneWarning ? "warning" : undefined}
        />
      </Space.Compact>
      {!hideErrorMessage && phoneError && (
        <div className="ant-form-item-explain ant-form-item-explain-error">{phoneError}</div>
      )}
      {!hideErrorMessage && !phoneError && phoneWarning && (
        <div className="ant-form-item-explain ant-form-item-explain-error">
          <div role="alert" className="ant-form-item-explain-error">
            {phoneWarning}
          </div>
        </div>
      )}
    </div>
  );
};

export const parsePhoneNumberByCountry = (countryCode?: string, fullPhoneNumber?: string) => {
  // replace all spaces
  const replacedFullPhoneNumber = fullPhoneNumber?.replace(/\s+/g, "") || "";
  const nationalityList = useMasterDataState.getState().nationality;
  const country = nationalityList.find((c) => c.code === countryCode);
  const dialCode = country?.dialCode;

  if (!dialCode || !replacedFullPhoneNumber?.startsWith(dialCode))
    return {
      dialCode: null,
      phoneNumber: null,
    };

  const numberWithoutDialCode = replacedFullPhoneNumber.slice(dialCode.length).trim();
  return {
    dialCode: dialCode,
    phoneNumber: numberWithoutDialCode,
  };
};

export default PhonePickerComponent;
